'use client';

import React, { useState } from 'react';
import { countries } from 'countries-list';
import GenerateButton from '@/components/ui/GenerateButton';
import AutoGrowingTextarea from '@/components/ui/AutoGrowingTextarea';

interface CountryOptions {
    count: number;
    allowDuplicates: boolean;
}

function GenerateCountryPage() {
    const [options, setOptions] = useState<CountryOptions>({
        count: 1,
        allowDuplicates: false,
    });

    const [generatedCountries, setGeneratedCountries] = useState<string>('');

    const countryNames = Object.values(countries).map(country => country.name);

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setOptions(prevOptions => ({
            ...prevOptions,
            [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
        }));
    };

    const generateCountries = () => {
        if (options.count < 1 || options.count > countryNames.length) {
            return;
        }
        let availableCountries = [...countryNames];
        let selected: string[] = [];

        for (let i = 0; i < options.count; i++) {
            if (availableCountries.length === 0) break;

            const randomIndex = Math.floor(Math.random() * availableCountries.length);
            const country = availableCountries[randomIndex];
            selected.push(country);

            if (!options.allowDuplicates) {
                availableCountries.splice(randomIndex, 1);
            }
        }

        setGeneratedCountries(selected.join('\n'));
    };

    return (
        <div className="w-full p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">Random Country Generator</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Configuration */}
                <div className="w-full md:w-1/2">
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Options</h2>
                        <label className="label">
                            <span className="label-text">Number of Countries (1-{countryNames.length})</span>
                        </label>
                        <input 
                            type="number" 
                            className={`input input-bordered w-full ${options.count < 1 || options.count > countryNames.length ? 'input-error' : ''}`}
                            name="count" 
                            min="1" 
                            max={countryNames.length}
                            value={options.count} 
                            onChange={handleOptionChange} 
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label cursor-pointer justify-start">
                            <input 
                                type="checkbox" 
                                className="checkbox checkbox-sm mr-2" 
                                name="allowDuplicates" 
                                checked={options.allowDuplicates} 
                                onChange={handleOptionChange} 
                            />
                            <span className="label-text">Allow Duplicates</span>
                        </label>
                    </div>
                    <GenerateButton generatePasswords={generateCountries} />
                </div>

                {/* Right Column - Generated Countries */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-xl font-semibold mb-2">Generated Countries</h2>
                    <AutoGrowingTextarea
                        value={generatedCountries}
                        minHeight={128}
                        maxHeight={512}
                    />
                </div>
            </div>
        </div>
    );
}

export default GenerateCountryPage;